import React, { useEffect, useRef, useState } from 'react';
import { numberWithCommas, valueOrZero } from 'spw-shared/utils';
import {
  JustifyStartRow,
  PaddingLeft8,
  PaddingRight8,
  SpaceBetweenRow,
} from 'spw-shared/globalComponents/grid';
import { FullWidthRow } from 'spw-components/floorplan/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import {
  ArrowContainer,
  HeaderValue,
  SpaceTypeNameContainer,
} from 'spw-components/worksheet/styles';
import {
  GridColumn,
  GridRow,
} from 'spw-programming-projects/building-details/worksheet/styles';
import { useSelector } from 'react-redux';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { MeasurementSystem, Unit } from 'spw-components/unit-conversion/types';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { Totals } from '../../../../models/Worksheet';
import WorksheetStorageSpaceRow from '../../../../models/WorksheetStorageSpaceRow';
import ChevronDown from '../../../../../static/images/chevron-down-outlined.svg';
import ChevronUp from '../../../../../static/images/chevron-up-outlined.svg';

interface ParentStorageSpaceTypeRowProps {
  name: string;
  row: WorksheetStorageSpaceRow;
  collapsed?: boolean;
  setCollapsed?: () => void;
  active: boolean;
  detailedIndividualConfig: boolean;
  showDecimals: boolean;
  spaceTypeTotals: Totals;
  shouldDisplayRowValues?: boolean;
  isPerDepartmentParentRow?: boolean;
}

export const ParentStorageSpaceTypeRow: React.FC<ParentStorageSpaceTypeRowProps> = ({
  name,
  row,
  setCollapsed,
  collapsed,
  active,
  showDecimals,
  spaceTypeTotals,
  shouldDisplayRowValues,
  isPerDepartmentParentRow,
}) => {
  const { convertToUnit } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);

  if (!row) return null;
  if (!row?.spaceType?.spaceType) {
    return null;
  }

  const arrowUpComponent = isPerDepartmentParentRow ? (
    <KeyboardArrowUp fontSize="small" />
  ) : (
    <img src={ChevronUp} width={15} alt="collapse" />
  );
  const arrowDownComponent = isPerDepartmentParentRow ? (
    <KeyboardArrowDown fontSize="small" />
  ) : (
    <img src={ChevronDown} width={15} alt="expand" />
  );

  const rowRef = useRef(null);

  // SPW-2108 For rendering the TPS we use this component (despite being called parent-space-type-row)
  // to store the "child" or nested rows. This is because the lowest level rows are linked department level
  const testingSuffix = row.child
    ? `child_${row.spaceType.id}`
    : row.spaceType.id;

  const [tempName, setTempName] = useState(null);

  // Sets the temporary name every time a space type row is renamed
  useEffect(() => {
    setTempName(name);
  }, [name]);

  const [hover, setHover] = useState(false);

  const {
    spacesQuantity,
    lockersOrDrawersQuantity,
    ratio,
    USF,
    dividendForRatio,
  } = spaceTypeTotals || {
    seats: 0,
    spacesQuantity: row.quantity,
    lockersOrDrawersQuantity: row.lockersOrDrawersQuantity,
    ratio: row.ratio,
    USF: row.quantity * row.usf,
    seatMix: 0,
    dividendForRatio: row.dividendForRatio || 0,
  };

  let className = '';

  if (hover || active) {
    className = className.concat(' hover');
  }

  if (row.removed) return <div />;

  const rowName = tempName;

  return (
    <div
      ref={rowRef}
      style={{
        // 80px for the worksheet header
        // then, we wanted to give a little more context to the user about where they are to avoid confusion.
        // 120px is the category header (80) + gray subheader (40)
        scrollMarginTop: '200px',
      }}
      data-cy={`parent_storage_space_type_row_${rowName}`}
    >
      <GridRow
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={className}
      >
        <GridColumn xs={3} className="row-start">
          <FullWidthRow>
            <SpaceBetweenRow width="100%">
              <JustifyStartRow onClick={setCollapsed}>
                <ArrowContainer>
                  {collapsed ? arrowDownComponent : arrowUpComponent}
                </ArrowContainer>
                <SpaceTypeNameContainer
                  style={{
                    wordBreak: 'normal',
                    overflowWrap: 'anywhere',
                    maxWidth: 103,
                    cursor: 'default',
                  }}
                  data-testid={`worksheet_row_name_${testingSuffix}`}
                >
                  {rowName}
                </SpaceTypeNameContainer>
              </JustifyStartRow>
            </SpaceBetweenRow>
          </FullWidthRow>
        </GridColumn>
        <GridColumn
          xs={1}
          style={{
            justifyContent: 'flex-start',
            paddingLeft: '8px',
          }}
        >
          <PaddingLeft8>
            <HeaderValue data-testid={`worksheet_row_ratio_${testingSuffix}`}>
              {`1:${numberWithCommas(ratio, showDecimals, true)}`}
            </HeaderValue>
          </PaddingLeft8>
        </GridColumn>
        <GridColumn xs={1}>
          <HeaderValue
            style={{ paddingRight: '6px' }}
            data-testid={`worksheet_row_qty_${testingSuffix}`}
          >
            {numberWithCommas(lockersOrDrawersQuantity, showDecimals, true)}
          </HeaderValue>
        </GridColumn>
        <GridColumn
          xs={1}
          data-testid={`worksheet_row_unitshigh_${testingSuffix}`}
        >
          {shouldDisplayRowValues
            ? numberWithCommas(row.lockersOrDrawersPerUnit, showDecimals, true)
            : null}
        </GridColumn>

        <GridColumn
          xs={1}
          data-testid={`worksheet_row_rowtotal_${testingSuffix}`}
        >
          {numberWithCommas(spacesQuantity, showDecimals, true)}
        </GridColumn>
        <GridColumn
          xs={2}
          className="seat-percent-column nowrap"
          data-testid={`worksheet_row_seatpercent_${testingSuffix}`}
        >
          1:
          {numberWithCommas(
            valueOrZero(dividendForRatio / spacesQuantity),
            showDecimals,
            true
          )}
        </GridColumn>
        <GridColumn xs={3} data-testid={`worksheet_row_usf_${testingSuffix}`}>
          <PaddingRight8>
            {shouldDisplayRowValues
              ? convertToUnit(row.usableSquareInches, {
                  unitType: 'area',
                  inputUnit: Unit.inches,
                  outputUnit:
                    measurementSystem === MeasurementSystem.imperial
                      ? Unit.inches
                      : Unit.meters,
                  formatter: (v) => numberWithCommas(v, showDecimals, true),
                })
              : null}
          </PaddingRight8>
        </GridColumn>
        <GridColumn
          xs={1}
          className="row-end"
          data-testid="row-usf"
          data-cy={`worksheet_row_totalusf_${testingSuffix}`}
        >
          {convertToUnit(USF, {
            unitType: 'area',
            formatter: (v) => numberWithCommas(v, showDecimals, true),
          })}
        </GridColumn>
      </GridRow>
    </div>
  );
};

export default ParentStorageSpaceTypeRow;
